"use client";

import React, { useState, useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { cn } from "@/app/_module/lib/utils";
import {
  A2PVerificationData,
  US_STATES,
} from "@/app/_module/types/a2p.types";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { Panel, Section, focusRing } from "./overview/primitives";
import { Field, ReadOnlyField, StepIntro, controlClass } from "./overview/wizard-ui";

// Helper functions for phone number formatting
const formatPhoneForDisplay = (value: string): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
  const limitedDigits = normalizedDigits.slice(0, 10);

  if (limitedDigits.length === 0) return '';
  if (limitedDigits.length <= 3) return `(${limitedDigits}`;
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
};

const formatPhoneForBackend = (value: string): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
  const limitedDigits = normalizedDigits.slice(0, 10);

  if (limitedDigits.length === 10) {
    return `+1${limitedDigits}`;
  }
  return limitedDigits;
};

// SSN Last 4 formatting helper - we only collect last 4 digits
const formatSSN4ForDisplay = (value: string): string => {
  if (!value) return '';
  // Only keep up to 4 digits
  const digits = value.replace(/\D/g, '').slice(0, 4);
  return digits;
};

export default function Step2SoleProprietorInfo() {
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<A2PVerificationData>();

  const { user } = useUserContext();
  const { branding } = useBranding();
  const [showSSN, setShowSSN] = useState(false);
  const input = (invalid: boolean) => cn("h-11", controlClass, invalid && "!border-red-700");

  // Auto-fill phone from user profile if not already set
  useEffect(() => {
    const currentPhone = getValues("businessPhone");
    if (!currentPhone && user?.user?.contactPhone) {
      setValue("businessPhone", formatPhoneForBackend(user.user.contactPhone));
    }
  }, [user, setValue, getValues]);

  return (
    <div className="space-y-8">
      <StepIntro title="About you">
        <p>
          You&apos;re registering as an individual. Enter your name exactly as it appears on your Social Security card.
          All fields are required.
        </p>
      </StepIntro>

      <Section id="a2p-you" title="Your details">
        <Panel className="space-y-5 px-4 py-5 sm:px-5">
          <Field
            id="businessName"
            label="Full legal name"
            hint="First, middle and last, as on your Social Security card."
            error={errors.businessName?.message}
          >
            <Input
              {...register("businessName", {
                required: "Enter your full legal name",
              })}
              id="businessName"
              placeholder="John Michael Smith"
              aria-invalid={!!errors.businessName}
              className={input(!!errors.businessName)}
            />
          </Field>

          <Field
            id="ssn"
            label="Last 4 digits of your SSN"
            hint="We only ask for the last 4 digits, never your full number."
            error={errors.ssn?.message}
          >
            <div className="relative max-w-xs">
              <Controller
                name="ssn"
                control={control}
                rules={{
                  required: "Enter the last 4 digits of your SSN",
                  validate: (value) => {
                    const digits = value?.replace(/\D/g, '') || '';
                    if (digits.length !== 4) {
                      return "Enter exactly 4 digits";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Input
                    id="ssn"
                    type={showSSN ? "text" : "password"}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={4}
                    value={formatSSN4ForDisplay(field.value || '')}
                    onChange={(e) => {
                      const input = e.target.value;
                      const digits = input.replace(/\D/g, '').slice(0, 4);
                      field.onChange(digits);
                    }}
                    aria-invalid={!!errors.ssn}
                    className={cn(input(!!errors.ssn), "pr-12 tabular-nums tracking-[0.2em]")}
                  />
                )}
              />
              <button
                type="button"
                onClick={() => setShowSSN(!showSSN)}
                aria-label={showSSN ? "Hide the digits" : "Show the digits"}
                aria-pressed={showSSN}
                className={cn(
                  "absolute right-1 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900",
                  focusRing,
                )}
              >
                {showSSN ? <EyeOff aria-hidden className="size-4" /> : <Eye aria-hidden className="size-4" />}
              </button>
            </div>
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="businessEmail" label="Email" error={errors.businessEmail?.message}>
              <Input
                {...register("businessEmail", {
                  required: "Enter your email",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Enter a valid email address",
                  },
                })}
                id="businessEmail"
                type="email"
                placeholder="john.smith@email.com"
                aria-invalid={!!errors.businessEmail}
                className={input(!!errors.businessEmail)}
              />
            </Field>

            <Field id="businessPhone" label="Mobile number" error={errors.businessPhone?.message}>
              <Controller
                name="businessPhone"
                control={control}
                rules={{
                  required: "Enter your mobile number",
                  validate: (value) => {
                    const digits = value?.replace(/\D/g, '') || '';
                    const normalizedDigits = digits.startsWith('1') && digits.length > 10
                      ? digits.slice(1)
                      : digits;
                    if (normalizedDigits.length !== 10) {
                      return "Enter a 10-digit mobile number";
                    }
                    return true;
                  },
                }}
                render={({ field }) => {
                  const displayValue = formatPhoneForDisplay(field.value || '');

                  return (
                    <Input
                      id="businessPhone"
                      type="tel"
                      inputMode="numeric"
                      placeholder="(555) 123-4567"
                      value={displayValue}
                      onChange={(e) => {
                        const input = e.target.value;
                        const newValue = input.replace(/[^\d]/g, '');
                        const existingDigits = (field.value || '').replace(/\D/g, '');
                        const normalizedExisting = existingDigits.startsWith('1') && existingDigits.length > 10
                          ? existingDigits.slice(1)
                          : existingDigits;

                        if (input.length < displayValue.length) {
                          const updatedDigits = normalizedExisting.slice(0, -1);
                          field.onChange(formatPhoneForBackend(updatedDigits));
                        } else {
                          field.onChange(formatPhoneForBackend(newValue));
                        }
                      }}
                      onKeyDown={(e) => {
                        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
                        if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      aria-invalid={!!errors.businessPhone}
                      className={cn(input(!!errors.businessPhone), "tabular-nums")}
                    />
                  );
                }}
              />
            </Field>
          </div>
          <p className="-mt-1 text-pretty text-xs leading-relaxed text-neutral-500">
            After our review you&apos;ll get a text on this number to confirm it&apos;s you, so use a mobile phone that
            receives texts.
          </p>

          {/* Website - locked for A2P compliance; the backend registers the business page. */}
          <ReadOnlyField
            label="Website"
            value="Set up for you"
            hint={`We register your ${branding.brandName} business page as your website, with our privacy policy and terms.`}
          />
        </Panel>
      </Section>

      <Section
        id="a2p-home"
        title="Home address"
        description="Your current home address. It has to match the address tied to your SSN."
      >
        <Panel className="space-y-5 px-4 py-5 sm:px-5">
          <Field id="street" label="Street address" error={errors.street?.message}>
            <Input
              {...register("street", {
                required: "Enter your street address",
              })}
              id="street"
              placeholder="123 Main Street, Apt 4B"
              aria-invalid={!!errors.street}
              className={input(!!errors.street)}
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="city" label="City" error={errors.city?.message}>
              <Input
                {...register("city", {
                  required: "Enter your city",
                })}
                id="city"
                placeholder="Detroit"
                aria-invalid={!!errors.city}
                className={input(!!errors.city)}
              />
            </Field>
            <Field id="state" label="State" error={errors.state?.message}>
              <Controller
                name="state"
                control={control}
                rules={{ required: "Choose a state" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="state" aria-invalid={!!errors.state} className={input(!!errors.state)}>
                      <SelectValue placeholder="Choose a state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="zipCode" label="ZIP code" error={errors.zipCode?.message}>
              <Input
                {...register("zipCode", {
                  required: "Enter your ZIP code",
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "Use 12345 or 12345-6789",
                  },
                })}
                id="zipCode"
                inputMode="numeric"
                placeholder="12345"
                maxLength={10}
                aria-invalid={!!errors.zipCode}
                className={cn(input(!!errors.zipCode), "tabular-nums")}
              />
            </Field>
            {/* Registered so the value submits; texting registration is US-only. */}
            <input type="hidden" {...register("country")} />
            <ReadOnlyField label="Country" value="United States" hint="Texting registration is for US residents only." />
          </div>
        </Panel>
      </Section>
    </div>
  );
}
